/**
 * The infra-red seeker on the rail: what it can see, what it has locked, and the tone that says so.
 *
 * ## The tone is the instrument
 *
 * A pilot flying a Sidewinder-armed aeroplane does not read a lock status off a screen. They
 * listen. The seeker's output is piped straight into the headset, and it *growls* — a rough,
 * low, wandering note whose pitch and roughness track how much infra-red energy the seeker head
 * is seeing. Point the aeroplane near a hot target and the growl rises; centre it and settle, and
 * the growl locks into a clean, steady, much higher tone. That transition is the whole interface,
 * and it is why {@link SeekerState.toneStrength} is a continuous number rather than a boolean —
 * the sound has to be able to say "nearly" as well as "yes".
 *
 * ## Aspect, and why a balloon cannot be locked
 *
 * What an IR seeker sees is heat, and an aeroplane's heat is mostly its tailpipe. So the same
 * target at the same range is several times brighter from behind than from head-on, and the
 * effective acquisition range varies with it. That is modelled here as an aspect gain on the
 * target's own {@link Engageable.infraredIntensity}, which means:
 *
 *   - a drone running away is acquirable a long way out;
 *   - the same drone coming at you has to be much closer;
 *   - a balloon, whose intensity is 0, cannot be acquired at any range at all.
 *
 * The last one is deliberate design rather than a side effect: the balloon course is a *gunnery*
 * exercise, and a seeker that could lock a balloon would turn it into four seconds of pressing
 * the missile button.
 *
 * ## What this is not
 *
 * There is no flare rejection, no scan pattern, no reticle slewing and no uncaged mode. Those are
 * the difference between a seeker and a study-level seeker, and the acceptance criterion here is
 * "tracks the jinking drone, evadable with a hard, well-timed break" — which is a property of the
 * *guidance* in `missile.ts`, not of the acquisition logic.
 *
 * `updateFixed(dt)`: yes — {@link Seeker.updateFixed}. Allocation-free.
 */

import { Vector3 } from 'three';
import { DEG2RAD, clamp01 } from '../../core/math.js';
import type { MissileConfig } from './config.js';
import type { TargetList } from './engagement.js';

export type SeekerStatus = 'none' | 'tracking' | 'locked';

/** What the seeker knows this tick. A plain struct: the HUD, the audio and the launch all read it. */
export interface SeekerState {
  status: SeekerStatus;
  /** Id of the tracked target, or −1. */
  targetId: number;
  /** Index into the target list, or −1. Revalidated every tick; never cached across ticks. */
  targetIndex: number;
  /** 0…1 — how much signal the head is seeing. Drives the growl. */
  toneStrength: number;
  /** 0…1 — how far through {@link MissileConfig.lockTimeS} the track has got. */
  lockProgress: number;
  /** Range to the tracked target [m], or 0. */
  rangeM: number;
  /** Closing speed [m/s], positive when the gap is shrinking. */
  closureMps: number;
  /** Is the tracked target inside the launch envelope right now? */
  inRange: boolean;
}

export function makeSeekerState(): SeekerState {
  return {
    status: 'none',
    targetId: -1,
    targetIndex: -1,
    toneStrength: 0,
    lockProgress: 0,
    rangeM: 0,
    closureMps: 0,
    inRange: false,
  };
}

/** What the seeker needs from the aircraft each tick. Owned by the caller and reused. */
export interface SeekerInput {
  /** Is the seeker powered and looking? False in free flight and with the gun selected. */
  active: boolean;
  /** Seeker head position, sim-frame NED [m] — near enough the aircraft's nose. */
  readonly positionNed: Vector3;
  /** Unit vector along the aircraft's nose, sim-frame NED. */
  readonly boresightNed: Vector3;
  /** The launch aircraft's velocity [m/s]. */
  readonly velocityNed: Vector3;
}

export function makeSeekerInput(): SeekerInput {
  return {
    active: false,
    positionNed: new Vector3(),
    boresightNed: new Vector3(1, 0, 0),
    velocityNed: new Vector3(),
  };
}

/**
 * How much of a target's infra-red output reaches a seeker looking at it from this angle.
 *
 * `aspect` is `losToTarget · targetVelocity`, both unit: +1 when the target is flying directly
 * away (the seeker is staring down the tailpipe) and −1 head-on. The 0.3 floor is the airframe's
 * own skin heating, which is what makes a head-on shot possible at all but only close in.
 *
 * Exported because the test suite asserts the tail/head ratio directly — it is the number that
 * decides whether running away from a missile is a good idea.
 */
export function aspectGain(aspect: number): number {
  return 0.3 + 0.7 * clamp01(aspect);
}

const toTarget = /* #__PURE__ */ new Vector3();
const relativeVelocity = /* #__PURE__ */ new Vector3();

export class Seeker {
  readonly state: SeekerState = makeSeekerState();

  private readonly config: MissileConfig;
  private readonly cosBoresight: number;
  private lockTimerS = 0;

  constructor(config: MissileConfig) {
    this.config = config;
    this.cosBoresight = Math.cos(config.boresightHalfAngleDeg * DEG2RAD);
  }

  /** Drop the track — a launch, a mode change, a respawn. */
  reset(): void {
    const state = this.state;
    state.status = 'none';
    state.targetId = -1;
    state.targetIndex = -1;
    state.toneStrength = 0;
    state.lockProgress = 0;
    state.rangeM = 0;
    state.closureMps = 0;
    state.inRange = false;
    this.lockTimerS = 0;
  }

  /**
   * Look, and update the track.
   *
   * The best target is re-chosen every tick from scratch rather than being latched, and the lock
   * timer only survives if the same target *id* wins again. That is what makes the lock feel
   * like a lock: let the nose drift off and the timer resets, so a solid tone genuinely means
   * the pilot held the target in the cone for the whole acquisition time.
   */
  updateFixed(dt: number, input: SeekerInput, targets: TargetList): void {
    const state = this.state;
    if (!input.active) {
      this.reset();
      return;
    }

    let bestIndex = -1;
    let bestScore = 0;
    let bestRange = 0;

    for (let i = 0; i < targets.length; i++) {
      const target = targets.at(i);
      if (!target.alive || target.infraredIntensity <= 0) continue;

      toTarget.subVectors(target.positionNed, input.positionNed);
      const range = toTarget.length();
      if (range < 1e-3 || range > this.config.maxRangeM) continue;

      const inverseRange = 1 / range;
      toTarget.multiplyScalar(inverseRange);
      // Inside the acquisition cone about the nose?
      if (toTarget.dot(input.boresightNed) < this.cosBoresight) continue;

      // Aspect: how much of the tailpipe is showing.
      const targetSpeed = target.velocityNed.length();
      const aspect =
        targetSpeed > 1e-3 ? toTarget.dot(target.velocityNed) / targetSpeed : 0;
      const gain = aspectGain(aspect);

      /*
       * The signal, and the range gate in one number.
       *
       * `effectiveRange` is how far this seeker could see *this* target from *this* angle, and
       * the score is how comfortably inside that the target actually is. A dim target head-on
       * scores below 0 and is skipped; the brightest, closest, most tail-on target wins.
       */
      const effectiveRange = this.config.maxRangeM * gain * clamp01(target.infraredIntensity);
      if (range > effectiveRange) continue;
      const score = 1 - range / effectiveRange;
      if (score <= bestScore) continue;

      bestScore = score;
      bestIndex = i;
      bestRange = range;
    }

    if (bestIndex < 0) {
      // Nothing in the cone. Drop everything — the growl dies with the track.
      state.status = 'none';
      state.targetId = -1;
      state.targetIndex = -1;
      state.toneStrength = 0;
      state.lockProgress = 0;
      state.rangeM = 0;
      state.closureMps = 0;
      state.inRange = false;
      this.lockTimerS = 0;
      return;
    }

    const target = targets.at(bestIndex);
    if (target.id !== state.targetId) {
      // A different target won: the acquisition starts again.
      this.lockTimerS = 0;
      state.targetId = target.id;
    }
    state.targetIndex = bestIndex;

    this.lockTimerS += dt;
    const progress = clamp01(this.lockTimerS / this.config.lockTimeS);
    state.lockProgress = progress;
    state.status = progress >= 1 ? 'locked' : 'tracking';

    toTarget.subVectors(target.positionNed, input.positionNed).multiplyScalar(1 / bestRange);
    relativeVelocity.subVectors(input.velocityNed, target.velocityNed);
    state.rangeM = bestRange;
    state.closureMps = relativeVelocity.dot(toTarget);
    state.inRange = bestRange >= this.config.minRangeM && bestRange <= this.config.maxRangeM;

    /*
     * The growl.
     *
     * Below lock it is the raw signal strength, so sweeping the nose across a target makes the
     * tone rise and fall the way it should. At lock it goes to 1 and stays there, which is what
     * the audio turns into the clean high tone.
     */
    state.toneStrength = progress >= 1 ? 1 : clamp01(bestScore) * (0.45 + 0.55 * progress);
  }
}
